import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import QRCodeSVG from 'qrcode.react';
import { makeStyles } from "@mui/styles";
import EllipsisContainer from "./EllipsisContainer";
import html2canvas from "html2canvas";
import axios from "axios";
import Utility from "../../Utility";
import { useStores } from "../../Hooks";
import ConditionalComponent from "./ConditionalComponent";
import GlobalUtility from "../../Utilities/GlobalUtility";

const qrColor = "#4990FE";
const topImageHeight = 26;
const topImageWidth = 138;
const topImageAspectRatio = topImageWidth / topImageHeight;

const useStyles = makeStyles(() => ({
    hidden: {
        display: "none"
    },
    root: {
        display: "flex",
        justifyContent: "center",
        flexDirection: "column",
        alignItems: "center",
    },
    btn: {
        border: `1px solid ${qrColor}`,
        width: 128,
        height: 23,
        color: qrColor,
        fontSize: 12,
        fontWeight: 600,
    },
    radiusContainer: {
        border: `3.5px solid ${qrColor}`,
        borderRadius: 8,


    }
}));


export const GenerateQRCode = observer(({
                                            link,
                                            logo,
                                            projectName,
                                            itemName,
                                            attachedItemName,
                                            topImage,
                                            borderColor,
                                            setImgURL,
                                            size,
                                            maxScroll,
                                            minScroll,
                                            setIsLoading,
                                            logoData,
                                            lastGeneratedSignature

                                        }) => {
    const classes = useStyles();
    const qrRoot = useRef(null);
    const isQrPage = GlobalUtility.verifyLastPath('qr-code');
    const isGenerating = useRef(false);

    useEffect(() => {
        const currentSignature = JSON.stringify({
            link, topImage, logo, projectName, itemName, borderColor, size
        });
        if (lastGeneratedSignature.current === currentSignature) {
            setIsLoading(false)
            return;
        }

        if (!!logo && !logoData) return;

        const timeoutId = setTimeout(() => {
            if (!qrRoot.current || isGenerating.current) return;

            isGenerating.current = true;
            setIsLoading(true);
            const scaleFactor = 1.5;
            html2canvas(qrRoot.current, {
                scale: scaleFactor,
                width: size + 14,
                height: (size * 1.11) + 70,
                logging: false,
            }).then(canvas => {
                const qrCtx = canvas.getContext('2d');
                const qrWidth = qrCtx.canvas.width / scaleFactor;
                const qrHeight = qrCtx.canvas.height / scaleFactor;
                const canvasContainer = document.createElement('canvas');
                const ctx = canvasContainer.getContext('2d');

                const finishGeneration = (finalUrl) => {
                    lastGeneratedSignature.current = currentSignature;
                    isGenerating.current = false;
                    setIsLoading(false);
                    setImgURL(finalUrl);
                };

                if (!!topImage) {
                    canvasContainer.width = qrWidth;
                    const topImageContainerHeight = ((qrWidth - 14) / topImageAspectRatio) + 7 + 4;
                    canvasContainer.height = qrHeight + topImageContainerHeight;
                    const img = new Image();
                    img.onload = function () {
                        const topImageCanvas = document.createElement('canvas');
                        const topImageCtx = topImageCanvas.getContext('2d');

                        const adjImgWidth = qrWidth - 14;
                        const adjImgHeight = adjImgWidth / topImageAspectRatio;
                        topImageCanvas.width = adjImgWidth;
                        topImageCanvas.height = adjImgHeight;
                        const newHeight = Math.min(img.width / topImageAspectRatio, img.height);
                        const startXPos = 0;
                        const startYPos = (img.height - newHeight) / 2;
                        topImageCtx.drawImage(img, startXPos, startYPos, img.width, newHeight, 0, 0, adjImgWidth, adjImgHeight);

                        ctx.fillStyle = '#FFFFFF';
                        ctx.fillRect(0, 0, qrWidth, topImageContainerHeight);
                        ctx.drawImage(topImageCanvas, 7, 7, adjImgWidth, adjImgHeight);
                        ctx.drawImage(canvas, 0, topImageContainerHeight, qrWidth, qrHeight);

                        ctx.strokeStyle = borderColor;
                        ctx.lineWidth = 5;
                        Utility.roundRectCorners(ctx, 7, topImageContainerHeight + 7, qrWidth - 14, qrWidth - 14, 10, 0.75, false, true);
                        canvasContainer.style.display = 'none';
                        finishGeneration(canvasContainer.toDataURL("jpeg"));
                    }
                    img.src = topImage;
                    img.crossOrigin = 'anonymous';
                } else {
                    canvasContainer.width = qrWidth + 14;
                    canvasContainer.height = qrHeight;
                    ctx.drawImage(canvas, 0, 0, qrWidth, qrHeight);
                    ctx.strokeStyle = borderColor;
                    ctx.lineWidth = 5;
                    Utility.roundRectCorners(ctx, 7, 7, qrWidth - 14, qrWidth - 14, 10, 0.75, false, true);
                    canvasContainer.style.display = 'none';
                    finishGeneration(canvasContainer.toDataURL("jpeg"));
                }
            }).catch(e => {
                console.error("QR Generation failed", e);
                isGenerating.current = false;
                setIsLoading(false);
            });
        }, 100);

        return () => clearTimeout(timeoutId);

    }, [link, topImage, logo, logoData, projectName, itemName, borderColor, size, setImgURL]);

    const isProcedure = projectName || itemName;

    function renderQR() {
        const imageSize = size / 6;
        const maxFontSize = 30, minFontSize = 10;
        const sliderPercent = (size - minScroll) / (maxScroll - minScroll);
        const fontSizeLerp = minFontSize + ((maxFontSize - minFontSize) * sliderPercent);

        const fullItemName = itemName + (attachedItemName ? ' - ' + attachedItemName : '');
        return (
            <ConditionalComponent if={!logo || (!!logo && !!logoData)}>
                <div className='qr-container' ref={qrRoot} key={Number(!!logoData)}>
                    <QRCodeSVG
                        renderAs='svg'
                        size={size}
                        id={`qr-${link}`}
                        value={link}
                        fgColor="black"
                        includeMargin
                        imageSettings={{
                            src: logoData || "",
                            x: null,
                            y: null,
                            height: imageSize,
                            width: imageSize,
                            excavate: true,
                        }}
                        level='H'
                    />
                    <ConditionalComponent if={isProcedure}>
                        <div className='details' style={{
                            backgroundColor: borderColor,
                            width: size - 12,
                            minHeight: 50,
                            fontSize: fontSizeLerp,
                            lineHeight: 1.5,
                            marginLeft: 5,
                            marginTop: 10,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            color: '#FFFFFF',
                            boxSizing: 'border-box',
                            overflow: 'hidden'
                        }}>
                            <ConditionalComponent if={!isQrPage}>
                                <EllipsisContainer value={projectName}>
                                    <div style={{ fontWeight: "bold" }}>
                                        {projectName}
                                    </div>
                                </EllipsisContainer>
                            </ConditionalComponent>
                            <EllipsisContainer value={fullItemName}>
                                <div style={{ marginBottom: 10, fontWeight: "bold" }}>
                                    {fullItemName}
                                </div>
                            </EllipsisContainer>
                        </div>
                    </ConditionalComponent>
                </div>
            </ConditionalComponent>
        );
    }

    return (
        <div className={classes.root}>
            {renderQR()}
        </div>
    )
});